import React, { createContext, useContext, useState, useEffect } from 'react';
import { checkWizAuth } from '../services/api';
import { error } from 'utils/logger';

interface WizContextType {
  wizConnected: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
  setWizConnected: (connected: boolean) => void;
  setIsConnecting: (connecting: boolean) => void;
  setIsDisconnecting: (disconnecting: boolean) => void;
}

const WizContext = createContext<WizContextType | undefined>(undefined);

export const WizProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [wizConnected, setWizConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const response = await checkWizAuth();
      setWizConnected(response.status === 1);
    } catch (err : any) {
      error('Error checking Wiz auth:', err);
      setWizConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only check auth if we have auth headers
    const { getAuthHeaders } = require('../services/api');
    const headers = getAuthHeaders();
    if (headers) {
      checkAuth();
    } else {
      setIsLoading(false);
      setWizConnected(false);
    }
  }, []);

  return (
    <WizContext.Provider value={{
      wizConnected,
      isConnecting,
      isDisconnecting,
      isLoading,
      checkAuth,
      setWizConnected,
      setIsConnecting,
      setIsDisconnecting
    }}>
      {children}
    </WizContext.Provider>
  );
};

export const useWiz = () => {
  const context = useContext(WizContext);
  if (context === undefined) {
    throw new Error('useWiz must be used within a WizProvider');
  }
  return context;
};
